<template>
  <v-layout column>
    <v-card>
      <v-card-title>
        <div class="headline">{{ $t('title') }}</div>
        <v-btn
          @click="refresh"
          color="primary"
          text
          icon
        >
          <v-tooltip color="primary" right>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">sync</v-icon>
            </template>
            <span>{{ $t('tips.refresh') }}</span>
          </v-tooltip>
        </v-btn>
        <v-spacer></v-spacer>
        <v-text-field
          append-icon="search"
          class="search"
          :label="$t('search')"
          single-line
          hide-details
          v-model="search"
        ></v-text-field>
      </v-card-title>
       <v-data-table
        :headers="headers"
        :items="items"
        :options="options"
        :search="search"
        sortBy="namespace"
        item-key="key"
        >
          <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
            <v-tooltip max-width="200" bottom :key="header.value">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ header.text }}
                </span>
              </template>
              <span>
                {{ header.tooltip }}
              </span>
            </v-tooltip>
          </template>
          <template v-slot:item.token="{ item }">
            <div v-clipboard:copy="item.token" @click="showToolTip(item.key)">
              <v-tooltip v-model="item.tooltip" color="primary" right>
                <template v-slot:activator="{ on }">
                  <span>...{{ item.token.slice(-20) }}</span>
                </template>
                <span>{{ $t('tips.copied') }}</span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.type="{ item }">
            <span class="capitalize" v-if="item.type">{{ item.type }}</span>
            <span v-else>&mdash;</span>
          </template>
          <template v-slot:item.status="{ item }">
            <span :class="item.status.warn ? 'error--text' : 'text'">{{ item.status.text }}</span>
          </template>
          <template v-slot:item.expiration="{ item }">
            <div
              v-if="item.unryoTokenExpiration"
              :class="item.unryoTokenExpiration.warn ? 'warning--text' : ''"
            >
              <v-tooltip color="primary" right>
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ item.unryoTokenExpiration.text }}</span>
                </template>
                <span>{{ item.unryoTokenExpiration.date }}</span>
              </v-tooltip>
            </div>
            <div v-else>{{ $t('none') }}</div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-menu>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <v-icon>more_vert</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(action, i) in actions"
                  :key="i"
                  @click="action.handler(item)"
                >
                  <v-list-item-title>{{ $t(action.name) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
    </v-card>
    <v-spacer></v-spacer>
    <div class="pending" v-if="pending">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <v-dialog
      v-model="dialogDelete"
      max-width="500"
    >
      <v-card>
        <v-card-title class="headline">{{ $t('dialog.delete.title') }}</v-card-title>
        <v-card-text class="error--text" v-if="errorDelete">{{ $t('dialog.delete.error') }}</v-card-text>
        <v-card-text v-else-if="currentToken.type === TokenType.SECRET">{{ $t('dialog.delete.warning_secret') }}</v-card-text>
        <v-card-text v-else>{{ $t('dialog.delete.warning') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="secondary"
            text
            @click="closeDialogDelete"
          >
            {{ $t('dialog.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            text
            :disabled="pending"
            :loading="pending"
            @click="deleteToken(currentToken)"
          >
            {{ $t('dialog.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import NetworkService from '@/services/network'
import formatDistance from 'date-fns/formatDistance'
import differenceInDays from 'date-fns/differenceInDays'
import { TokenType, TokenUser } from '@/model/token'

export default {
  data () {
    return {
      currentToken: false,
      search: '',
      options: {
        itemsPerPage: 10
      },
      headers: [
        {
          align: 'left',
          text: this.$t('columns.tokens'),
          value: 'token',
          width: '10%',
          tooltip: this.$t('tips.token')
        },
        {
          align: 'left',
          text: this.$t('columns.namespaces'),
          value: 'namespace',
          width: '10%',
          tooltip: this.$t('tips.namespace')
        },
        {
          align: 'left',
          text: this.$t('columns.type'),
          value: 'type',
          width: '10%',
          tooltip: this.$t('tips.type')
        },
        {
          align: 'left',
          text: this.$t('columns.status'),
          value: 'status',
          width: '10%',
          tooltip: this.$t('tips.status')
        },
        {
          align: 'left',
          text: this.$t('columns.expiration'),
          value: 'expiration',
          width: '5%',
          tooltip: this.$t('tips.expiration')
        },
        {
          align: 'left',
          text: this.$t('columns.actions'),
          value: 'actions',
          width: '5%',
          tooltip: this.$t('tips.actions')
        }
      ],
      items: [],
      actions: [
        {
          name: this.$t('actions.enable'),
          handler: this.enableToken
        },
        {
          name: this.$t('actions.revoke'),
          handler: this.revokeToken
        },
        {
          name: this.$t('actions.delete'),
          handler: this.showDialogDelete
        }
      ],
      pending: false,
      dialogDelete: false,
      errorDelete: false,
      TokenType
    }
  },
  created () {
    this.fetchTokens()
  },
  methods: {
    fetchTokens () {
      this.pending = true
      return NetworkService.namespaces()
      .then((namespaces) =>
        Promise.all(namespaces.map((ns) =>
          Promise.all([ ns, NetworkService.namespaceTokens(ns) ])
        ))
      )
      .then((results) => {
        const items = []
        for (const [namespace, tokens] of results) {
          for (let token of tokens) {
            token = this.toUnryoToken(token, namespace)
            items.push(token)
          }
        }
        this.items = items
      })
      .finally(() => {
        this.pending = false
      })
    },
    deleteToken (token) {
      this.pending = true
      let promise
      const remainingTokens = this.items.filter((t) => t.namespace === token.namespace)
      if (remainingTokens.length === 1) {
        promise = NetworkService.deleteNamespace(token.namespace)
      } else {
        promise = NetworkService.deleteToken(token.namespace, token.id)
      }
      promise.then((data) => {
        this.items = this.items.filter((t) => t.key !== token.key)
        this.closeDialogDelete()
      })
      .catch(() => {
        this.errorDelete = true
      })
      .finally(() => {
        this.pending = false
      })
    },
    enableToken (token) {
      this.pending = true
      const d = new Date()
      const payload = {
        value: {
          expiration: null
        }
      }
      NetworkService.editToken(token.namespace, token.id, payload)
      .then((data) => {
        const index = this.items.findIndex((item) => item.key === token.key)
        this.$set(this.items, index, this.toUnryoToken(data, token.namespace))
      })
      .finally(() => {
        this.pending = false
      })
    },
    revokeToken (token) {
      this.pending = true
      const payload = {
        value: {
          expiration: 0
        }
      }
      NetworkService.editToken(token.namespace, token.id, payload)
      .then((data) => {
        const index = this.items.findIndex((item) => item.key === token.key)
        this.$set(this.items, index, this.toUnryoToken(data, token.namespace))
      })
      .finally(() => {
        this.pending = false
      })
    },
    refresh () {
      this.items = []
      this.fetchTokens()
    },
    showDialogDelete (token) {
      this.currentToken = token
      this.dialogDelete = true
    },
    closeDialogDelete () {
      this.dialogDelete = false
      this.errorDelete = false
    },
    toUnryoToken (token, namespace) {
      const { expiration } = token
      const unryoToken = { ...token, namespace }
      unryoToken.key = this.buildKey(unryoToken.namespace, unryoToken.id)
      unryoToken.status = {}
      if (expiration === undefined || expiration > Date.now()) {
        unryoToken.status.text = this.$t('status.active')
      } else {
        unryoToken.status.warn = true
        if (expiration <= 0) {
          unryoToken.status.text = this.$t('status.revoked')
        } else {
          unryoToken.status.text = this.$t('status.expired')
        }
      }
      if (expiration && expiration > 0) {
        unryoToken.unryoTokenExpiration = {
          date: new Date(expiration).toLocaleDateString(),
          text: formatDistance(Date.now(), expiration, { addSuffix: true }),
          warn: differenceInDays(expiration, Date.now()) <= 30
        }
      }
      if (unryoToken.type === undefined) {
        unryoToken.type = TokenType.NODE
      }
      unryoToken.tooltip = false
      return unryoToken
    },
    buildKey (namespace, id) {
      return namespace + '/' + id
    },
    showToolTip (key) {
      const index = this.items.findIndex((item) => item.key === key)
      this.items[index].tooltip = true
      setTimeout(() => { this.closeToolTip(index) }, 2000)
    },
    closeToolTip (index) {
      this.items[index].tooltip = false
    }
  }
}
</script>

<style lang="scss" scoped>
.pending {
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  position: absolute;
  text-align: center;

  > .v-progress-circular {
    top: calc(50% - 35px);
  }
}

.search {
  max-width: 25rem;
}


.v-data-table {
  .capitalize {
    text-transform: capitalize;
  }
}
</style>

<i18n>
{
  "en": {
    "title": "Token Management",
    "search": "Search",
    "none": "None",
    "tips": {
      "copied": "Copied!",
      "refresh": "Refresh tokens.",
      "token": "Tokens are unique identifier associated with your organization. For your protection, treat them as you would a password. Never share tokens with other users or application.",
      "namespace": "Environment where the components belong. It is used to create a network of services, to implement multitenancy among the same owner of applications.",
      "type": "Type of the token.",
      "status": "Can be ACTIVE, EXPIRED or REVOKED. Component using non-active token are not registered to the Unryo cluster.",
      "expiration": "None or remaining time until expiration.",
      "actions": "Enable/Revoke/Delete. If revoked, components using this token will be unregistered from the Unryo cluster. They might be still running and consuming resources until their container is stopped or removed."
    },
    "columns": {
      "tokens": "Tokens",
      "namespaces": "Namespaces",
      "status": "Status",
      "type": "Type",
      "expiration": "Expiration",
      "actions": "Actions"
    },
    "actions": {
      "delete": "Delete",
      "enable": "Enable",
      "revoke": "Revoke"
    },
    "status": {
      "active": "ACTIVE",
      "expired": "EXPIRED",
      "revoked": "REVOKED"
    },
    "dialog": {
      "confirm": "Confirm",
      "cancel": "Cancel",
      "delete": {
        "title": "Are you sure you want to delete it ?",
        "warning": "Warning: once the token is deleted it can't be recovered.",
        "warning_secret": "Warning: once the secret token is deleted it can't be recovered.",
        "error": "An error occurred, please try again later."
      }
    }
  }
}
</i18n>